import React, { useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Grid,
  GridItem,
  HStack,
  Icon,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  BsCheckCircleFill,
  BsChevronDown,
  BsChevronUp,
  BsExclamationCircle,
} from "react-icons/bs";
import RiskReviewFieldRenderer from "./RiskReviewFieldRenderer";
import RiskReviewFinancialSummary from "./RiskReviewFinancialSummary";
import {
  getSectionCompletion,
  sortBySequence,
} from "../../utils/riskReviewFormUtils";

const isFinancialSection = (fields = []) =>
  fields.filter((field) =>
    ["CURRENCY", "PERCENTAGE", "CALCULATED"].includes(field.inputType),
  ).length >= 3;

const RiskReviewSection = ({
  section,
  control,
  values,
  rules,
  errors,
  editable,
  requestId,
}) => {
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const panelBg = useColorModeValue("white", "gray.800");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const bodyBorderColor = useColorModeValue("gray.100", "gray.700");
  const hoverBorderColor = useColorModeValue("gray.300", "gray.600");
  const titleColor = useColorModeValue("gray.800", "gray.100");
  const subtleShadow = useColorModeValue(
    "0 1px 2px rgba(16,24,40,0.04)",
    "0 1px 2px rgba(0,0,0,0.24)",
  );
  const hoverShadow = useColorModeValue(
    "0 4px 12px rgba(16,24,40,0.06)",
    "0 6px 16px rgba(0,0,0,0.28)",
  );

  const isCollapsible = section.collapsible !== false;

  // Collapsible sections always start CLOSED.
  // Non-collapsible sections always stay OPEN.
  const [isExpanded, setIsExpanded] = useState(() => !isCollapsible);

  const expanded = !isCollapsible || isExpanded;

  const completion = getSectionCompletion({
    section,
    values,
    required: rules.required,
    visibility: rules.visibility,
  });

  const hasErrors = (section.fields || []).some((field) => errors[field.code]);

  const fields = sortBySequence(section.fields || []);

  const questionIndexMap = fields
    .filter((field) => field.inputType?.startsWith("QUESTION"))
    .reduce(
      (map, field, index) => ({
        ...map,
        [field.code]: index,
      }),
      {},
    );

  const handleAccordionChange = (nextIndex) => {
    if (!isCollapsible) return;

    setIsExpanded(nextIndex === 0);
  };

  return (
    <Accordion
      allowToggle
      index={expanded ? 0 : -1}
      onChange={handleAccordionChange}
    >
      <AccordionItem
        bg={panelBg}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="xl"
        boxShadow={subtleShadow}
        overflow="hidden"
        transition="all 0.18s ease"
        _hover={{
          borderColor: hoverBorderColor,
          boxShadow: hoverShadow,
        }}
      >
        <AccordionButton
          position="relative"
          px={5}
          py={4}
          cursor={isCollapsible ? "pointer" : "default"}
          _before={{
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "3px",
            bg: expanded ? "blue.500" : "transparent",
          }}
          _hover={{
            bg: isCollapsible ? hoverBg : "transparent",
          }}
        >
          <Box flex="1" textAlign="left">
            <HStack spacing={2} wrap="wrap">
              {hasErrors ? (
                <BsExclamationCircle color="var(--chakra-colors-red-500)" />
              ) : completion.total > 0 && completion.isComplete ? (
                <BsCheckCircleFill color="var(--chakra-colors-green-500)" />
              ) : null}

              <Text fontSize="md" fontWeight="700" color={titleColor}>
                {section.name}
              </Text>
            </HStack>

            {section.description ? (
              <Text color="gray.500" fontSize="sm" mt={1}>
                {section.description}
              </Text>
            ) : null}
          </Box>

          <Box
            px={2.5}
            py={1}
            borderRadius="full"
            bg={completion.total > 0 && completion.isComplete ? "green.50" : "gray.100"}
            mr={3}
            flexShrink={0}
          >
            <Text
              color={
                completion.total > 0 && completion.isComplete
                  ? "green.700"
                  : "gray.600"
              }
              fontSize="xs"
              fontWeight="600"
            >
              {completion.total
                ? `${completion.complete}/${completion.total}`
                : `${fields.length} fields`}
            </Text>
          </Box>

          {isCollapsible && (
            <Icon
              as={expanded ? BsChevronUp : BsChevronDown}
              color="gray.500"
              boxSize={4}
            />
          )}
        </AccordionButton>

        <AccordionPanel
          p={5}
          borderTopWidth="1px"
          borderColor={bodyBorderColor}
        >
          {isFinancialSection(fields) ? (
            <RiskReviewFinancialSummary
              fields={fields}
              control={control}
              rules={rules}
              errors={errors}
              editable={editable}
            />
          ) : (
            <Grid templateColumns="repeat(12, 1fr)" gap={4}>
              {fields
                .filter((field) => rules.visibility[field.code] !== false)
                .map((field) => (
                  <GridItem
                    key={field.code}
                    colSpan={{
                      base: 12,
                      md: Math.min(
                        field.uiConfig?.width ||
                          (field.inputType === "TEXTAREA" ||
                          field.inputType?.startsWith("QUESTION")
                            ? 12
                            : 6),
                        12,
                      ),
                    }}
                  >
                    <RiskReviewFieldRenderer
                      field={field}
                      control={control}
                      disabled={!editable || rules.disabled[field.code]}
                      error={errors[field.code]}
                      required={rules.required[field.code]}
                      index={questionIndexMap[field.code]}
                      requestId={requestId}
                    />
                  </GridItem>
                ))}
            </Grid>
          )}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

export default RiskReviewSection;
